/**
 * `AudioParam` writes — the one place in the audio system that is allowed to touch a parameter.
 *
 * ## Never assign, always glide
 *
 * `param.value = x` is a step change. On a gain it is a click; on a filter cutoff it is a tick;
 * on an oscillator frequency it is a chirp. There is one continuous soundscape in this
 * milestone's acceptance list and "no clicks, pops or looping artifacts" is the first thing it
 * asks for, so nothing outside this file assigns to a parameter after construction.
 * {@link setTarget} schedules an exponential approach instead, which the audio thread renders
 * per sample.
 *
 * ## Why the redundancy filter matters
 *
 * `setTargetAtTime` appends to the parameter's automation timeline. Called every frame on every
 * parameter, at 120 fps, across the sixty-odd parameters this system owns, that is 7 000 timeline
 * events a second whose only effect is to restate a value that has not changed. The audio budget
 * for the whole system is 0.5 ms a frame; this is where a good part of it would go.
 *
 * So each parameter's last *requested target* is remembered, and a write that would not move it
 * by a meaningful fraction is dropped. The threshold is relative, because these parameters span
 * six orders of magnitude — 0.0001 is a real change to a gain and noise on a 20 kHz cutoff.
 *
 * A `WeakMap` rather than a field on a wrapper object: parameters are owned by nodes, nodes get
 * disposed, and a strong map would keep every filter of every one-shot alive for the session.
 *
 * `updateFixed` / `updateFrame`: called from the audio system's frame update. Allocation-free —
 * `WeakMap.get`/`set` on an existing key does not allocate.
 */

/** Last requested target per parameter, so an unchanged write can be skipped. */
const lastTarget = new WeakMap<AudioParam, number>();

/**
 * Relative movement below which a write is considered a no-op.
 *
 * 0.1 % of the larger of the two values. Well under anything audible on a gain (0.001 dB), well
 * under the frequency resolution of the ear on a cutoff, and enough to swallow the last-bit
 * jitter of a value derived from a float physics state that is technically never still.
 */
const EPSILON_RELATIVE = 1e-3;

/**
 * Glide `param` toward `value` with time constant `timeConstantS`.
 *
 * The time constant is the *e-folding* time, not the arrival time: after one constant the
 * parameter has covered 63 % of the distance, after three 95 %. Pick it from what the thing
 * physically is — a throttle change is tenths of a second, a bus volume is tens of milliseconds,
 * a filter tracking airspeed is slower than one tracking a gunshot.
 */
export function setTarget(
  param: AudioParam,
  value: number,
  timeConstantS: number,
  nowS: number,
): void {
  if (!Number.isFinite(value)) return;
  const previous = lastTarget.get(param);
  if (previous !== undefined && !moved(previous, value)) return;
  lastTarget.set(param, value);
  // A zero time constant is not legal and, on the implementations that accept it, is a step
  // change — which is the thing this module exists to prevent.
  param.setTargetAtTime(value, nowS, Math.max(1e-4, timeConstantS));
}

/**
 * Set `param` immediately, cancelling any automation.
 *
 * For construction and for the start of a one-shot's envelope, where there is nothing to glide
 * from. Never for a continuous parameter of a running voice.
 */
export function setImmediate(param: AudioParam, value: number, nowS: number): void {
  if (!Number.isFinite(value)) return;
  lastTarget.set(param, value);
  param.cancelScheduledValues(nowS);
  param.setValueAtTime(value, nowS);
}

/**
 * A percussive envelope: jump to `peak` over `attackS`, then decay toward zero over `decayS`.
 *
 * Used by every one-shot — the touchdown thump, the tyre chirp, a switch click, the gun. The
 * attack is a linear ramp rather than an instant jump because an instant jump *is* a click: the
 * click you want in a switch sound comes from the ramp being one millisecond, not from it being
 * zero. The decay is exponential, because that is what a struck object does.
 *
 * `setValueAtTime(0)` first so that a one-shot fired while a previous one is still ringing
 * restarts cleanly rather than summing into it.
 */
export function percussive(
  param: AudioParam,
  peak: number,
  attackS: number,
  decayS: number,
  nowS: number,
): void {
  if (!Number.isFinite(peak) || peak <= 0) return;
  lastTarget.delete(param);
  param.cancelScheduledValues(nowS);
  param.setValueAtTime(0, nowS);
  param.linearRampToValueAtTime(peak, nowS + Math.max(0.0005, attackS));
  // `setTargetAtTime` rather than `exponentialRamp`, because an exponential ramp cannot end at
  // zero and the workaround (ramp to 1e-4, then step) is itself a step.
  param.setTargetAtTime(0, nowS + Math.max(0.0005, attackS), Math.max(1e-3, decayS / 3));
}

function moved(previous: number, next: number): boolean {
  const scale = Math.max(Math.abs(previous), Math.abs(next), 1e-9);
  return Math.abs(next - previous) > EPSILON_RELATIVE * scale;
}
